import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ButtonV2,
  LoadingLogo,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import { makeStyles } from 'tss-react/mui'
import {
  API,
  LayoutWrapper,
  LoadingTypes,
  Milestones,
  NoHeaderLayoutWrapper,
  RouteApp,
  SelectedCloudIcon,
  crmServiceUrl,
  isLoading,
  useAccountId,
  useCurrentCloudProvider,
  useSalesforceContext,
  useUserType,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import {
  ExternalPlatformType,
  ProductListingV2,
} from '../components/ProductListingV2'
import { CloudAndProductNameTitle } from '../components/CloudAndProductNameTitle'
import { RenewOfferSubscription } from '../components/Subscription/RenewOfferSubscription/RenewOfferSubscription'
import type { RenewOfferSubscriptionProps } from '../components/Subscription/RenewOfferSubscription/renewOfferTypes'
import SubscriptionDetail, {
  type SubscriptionDetailProps,
} from '../components/Subscription/SubscriptionDetail/SubscriptionDetail'
import { AddEditChannelOffer } from '../components/ChannelOffers'
import { useGetProductDetail } from '../common/utils/hooks/useGetProductDetail'
import { useSyncFlyoutRouteParams } from '../common/utils/hooks/useSyncFlyoutRouteParams'
import { AddEditResellerAuthorization } from '../components/AddEditResellerAuthorization/AddEditResellerAuthorization'
import { EditResaleAuthorizationWrapper } from '../components/oldResaleAuth/EditResaleAuthorizationWrapper/EditResaleAuthorizationWrapper'
import {
  EditPrivateOfferWrapper,
  EditPrivateOfferWrapperV2 as MarketplaceEditPrivateOfferWrapperV2,
  NewPrivateOfferWrapper,
} from '../components/NewPrivateOfferWrapper'
import {
  DetailedViewPrivateOfferWrapper as MarketplaceDetailedViewPrivateOfferWrapper,
  type DetailedViewPrivateOfferWrapperProps,
} from '../components/DetailedViewPrivateOfferWrapper'
import { MarketplaceMappingManager as MarketplaceMappingManagerUI } from '../components/MarketplaceMappingManager'
import { PartnerAccountDataProvider } from '../external/PartnerAccountDataProvider/PartnerAccountDataProvider'
import { customerFormData } from '../components/MarketplaceMappingManager/utils/customerFormData'
import { CRMTypes } from '../utils/constants'
import { useSelector } from '../store'
import crmConnectionImage from '../common/images/MarketplaceTemplate.svg'
import {
  dimensionsUpdatedInListingMessage,
  productDimensionsUpdatedInListingMessage,
  usageDimensionsUpdatedInListingMessage,
} from '../common/utils/messagesContants'

const dimensionUpdateCopy = {
  both: {
    title: 'Dimensions updated',
    description: dimensionsUpdatedInListingMessage,
  },
  product: {
    title: 'Product dimensions updated',
    description: productDimensionsUpdatedInListingMessage,
  },
  usage: {
    title: 'Usage dimensions updated',
    description: usageDimensionsUpdatedInListingMessage,
  },
} as const

const UsageDimensionsUpdatedPopup = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dimensionUpdate = new URLSearchParams(location.search).get(
    'dimensionUpdate'
  )
  const copy =
    dimensionUpdate === 'both' ||
    dimensionUpdate === 'product' ||
    dimensionUpdate === 'usage'
      ? dimensionUpdateCopy[dimensionUpdate]
      : undefined
  const [open, setOpen] = useState(() => Boolean(copy))
  const close = useCallback(() => {
    setOpen(false)
    const params = new URLSearchParams(location.search)
    params.delete('dimensionUpdate')
    const search = params.toString()
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate])

  if (!copy) {
    return null
  }

  return (
    <ModalConfirmV2
      title={copy.title}
      description={copy.description}
      open={open}
      titleAcceptButton="Okay"
      titleCancelButton="Close"
      onAccept={close}
      onDecline={close}
      variant={ModalSizeVariants.small}
      hideCancelButton={false}
      hideCancelIcon={false}
    />
  )
}

export const RenewOfferSubscriptionWrapper = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  crmObjectLink,
  isSalesforce,
  steps,
}: RenewOfferSubscriptionProps) => {
  return (
    <RenewOfferSubscription
      externalBaseURL={externalBaseURL}
      search={search}
      crmOpportunityId={crmOpportunityId}
      crmObjectLink={crmObjectLink}
      isSalesforce={isSalesforce}
      steps={steps}
    />
  )
}

export const SubscriptionDetailWrapper = ({
  isBack,
}: SubscriptionDetailProps) => <SubscriptionDetail isBack={isBack} />

export const ChannelOfferEditWrapper = () => {
  const { cloud, accountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const navigate = useNavigate()

  const handleBack = useCallback(() => {
    if (cloud && accountId) {
      navigate(
        `/flyout/${cloud}/${accountId}/offers-authorizations/channel-offers`
      )
    } else {
      navigate('/flyout/offers-authorizations/channel-offers')
    }
  }, [cloud, accountId, navigate])

  return (
    <LayoutWrapper
      title={'Create a Channel offer'}
      component={AddEditChannelOffer}
      isSidePanelOpened={false}
      actions={Milestones}
      isBack
      onBack={handleBack}
    />
  )
}

export const EditPrivateOfferWrapperV2 = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  crmObjectLink,
  isSidePanelVisible,
}: {
  externalBaseURL?: string
  search?: string
  crmOpportunityId?: string
  crmObjectLink?: string
  isSidePanelVisible?: boolean
}) => {
  const salesforceContext = useSalesforceContext()
  const isSalesforceContext =
    salesforceContext.error !== 'salesforce_context_not_available' &&
    !isEmpty(salesforceContext.partnerId)

  return (
    <>
      <UsageDimensionsUpdatedPopup />
      <MarketplaceEditPrivateOfferWrapperV2
        externalBaseURL={externalBaseURL}
        search={search}
        crmOpportunityId={crmOpportunityId}
        crmObjectLink={crmObjectLink}
        isSidePanelVisible={isSidePanelVisible}
        isSalesforceContext={isSalesforceContext}
      />
    </>
  )
}

export const CreateEditPrivateOfferWrapper = memo(() => {
  const {
    productId = '',
    cloud,
    accountId,
  } = useParams<{
    productId: string
    cloud: string
    accountId: string
  }>()
  const productFetchingLoading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.FLYOUT_PRODUCT_DETAIL)
  )
  const productsFetchingLoading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  const { search } = useLocation()
  const fromValue = new URLSearchParams(search).get('from') || ''
  const cloudMarketplace = useCurrentCloudProvider()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const routeAccountId = accountId || fallbackAccountId
  const selectedProduct = useGetProductDetail({
    accountId: routeAccountId,
    productId,
    cloudMarketplace,
  })
  const backLink = useMemo(() => {
    return cloud && routeAccountId
      ? `/flyout/${cloud}/${routeAccountId}/offers-authorizations/private-offers`
      : '/flyout/offers-authorizations/private-offers'
  }, [cloud, routeAccountId])
  const PrivateOfferTitle = useCallback(
    () => <CloudAndProductNameTitle selectedProduct={selectedProduct} />,
    [selectedProduct]
  )
  const v2Offer = useMemo(() => {
    return (
      cloudMarketplace === 'AZURE' ||
      (cloudMarketplace === 'AWS' &&
        (selectedProduct?.productType === PlanType.CONTAINER ||
          selectedProduct?.productType === PlanType.SAAS_CONTRACT ||
          selectedProduct?.productType === PlanType.AMI ||
          selectedProduct?.productType === PlanType.SAAS_SUBSCRIPTION))
    )
  }, [cloudMarketplace, selectedProduct])

  if (
    productFetchingLoading ||
    productsFetchingLoading ||
    (cloudMarketplace === 'AWS' && !selectedProduct)
  ) {
    return (
      <>
        <UsageDimensionsUpdatedPopup />
        <LoadingLogo />
      </>
    )
  }

  return (
    <div data-testid="create-edit-wrapper">
      {v2Offer ? (
        <LayoutWrapper
          title={
            fromValue === 'clone'
              ? 'Clone private offer'
              : 'Create a private offer'
          }
          component={EditPrivateOfferWrapperV2}
          isBack={true}
          actions={SelectedCloudIcon}
          isSidePanelOpened={false}
          customNavButton={{
            ButtonName: 'Back to Private offers',
            link: backLink,
            shouldShowCustomNavButton: true,
          }}
        />
      ) : (
        <RouteApp
          titleIcon={<HandshakeOutlined fontSize="medium" />}
          title="New private offer"
          component={EditPrivateOfferWrapper}
          actions={PrivateOfferTitle}
        />
      )}
    </div>
  )
})

export const DetailedViewPrivateOfferWrapper = ({
  crmType,
  crmOpportunityId,
  externalBaseURL,
  search,
  onBack,
}: Omit<DetailedViewPrivateOfferWrapperProps, 'partnerId'>) => {
  const { partnerId } = useSalesforceContext()

  return (
    <MarketplaceDetailedViewPrivateOfferWrapper
      crmType={crmType}
      crmOpportunityId={crmOpportunityId}
      externalBaseURL={externalBaseURL}
      search={search}
      onBack={onBack}
      partnerId={partnerId}
    />
  )
}

const useCrmConnectionStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    height: theme.spacing(32),
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(6),
    backgroundColor: theme.palette.text.white,
  },
  templateIcon: {
    height: theme.spacing(15.75),
    width: theme.spacing(21.5),
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  heading: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: theme.typography.subtitle1.lineHeight,
    color: theme.palette.labraText.primary,
    fontFamily: theme.typography.fontFamily,
    letterSpacing: theme.typography.subtitle1.letterSpacing,
  },
  subHeading: {
    fontSize: theme.typography.body2.fontSize,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: theme.typography.body2.lineHeight,
    color: theme.palette.labraText.description,
    fontFamily: theme.typography.fontFamily,
    letterSpacing: theme.typography.body2.letterSpacing,
  },
  button: {
    marginTop: theme.spacing(1),
  },
}))

const CrmConnectionPending = () => {
  const { classes } = useCrmConnectionStyles()
  const navigate = useNavigate()

  return (
    <div className={classes.root} data-testid="cosell-crm-page">
      <img
        src={crmConnectionImage}
        alt="crm image"
        className={classes.templateIcon}
        data-testid="co-sell-crm-image"
      />
      <div className={classes.textContainer} data-testid="empty-state-text">
        <div className={classes.heading}>Connect CRM Pending</div>
        <div className={classes.subHeading}>
          To get started with mapping manager or sync configurations, please
          connect your CRM by clicking on the button below.
        </div>
        <div className={classes.button}>
          <ButtonV2
            data-testid="connect-your-crm-button"
            styleType="primary"
            onClick={() => navigate('/settings/crm')}
          >
            Connect your CRM
          </ButtonV2>
        </div>
      </div>
    </div>
  )
}

type CrmAuthForm = {
  deleted_at?: string
  creds?: unknown[]
}

export const MarketplaceMappingManager = () => {
  const partnerType = useUserType()
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const { crmType } = customerFormData()
  const crmFormData = useSelector<CrmAuthForm | null | undefined>(state => {
    const authState = state as typeof state & {
      CRMAuth?: { crmFormData?: CrmAuthForm | null }
    }
    return authState.CRMAuth?.crmFormData
  })
  const [fetchedConnected, setFetchedConnected] = useState<boolean | null>(null)
  const isHubspot = crmType === CRMTypes.hubspot
  const storedConnected = crmFormData
    ? !isEmpty(crmFormData) &&
      (crmFormData.creds || []).length > 0 &&
      isEmpty(crmFormData.deleted_at)
    : null
  const isCrmConnected = storedConnected ?? fetchedConnected ?? false

  useEffect(() => {
    if (!isHubspot || !crmId || storedConnected !== null) {
      return
    }

    let cancelled = false
    API.get(`${crmServiceUrl}/crms/${crmId}?includes=creds,crm_app_creds`)
      .then(response => {
        if (cancelled) return
        const data = response?.data as CrmAuthForm | undefined
        setFetchedConnected(
          !isEmpty(data) &&
            (data?.creds || []).length > 0 &&
            isEmpty(data?.deleted_at)
        )
      })
      .catch(() => {
        if (!cancelled) setFetchedConnected(false)
      })

    return () => {
      cancelled = true
    }
  }, [crmId, isHubspot, storedConnected])

  return (
    <MarketplaceMappingManagerUI
      isCrmSelected={isHubspot ? isCrmConnected : true}
      crmConnectionPage={<CrmConnectionPending />}
    />
  )
}

export const ImpartnerProductListingWrapper = () => (
  <PartnerAccountDataProvider isExternal={true}>
    <ProductListingV2 externalPlatformType={ExternalPlatformType.IMPARTNER} />
  </PartnerAccountDataProvider>
)

export const ViewPrivateOfferDetails = () => {
  const { productId, accountId } = useParams<{
    productId: string
    accountId: string
  }>()
  const cloudMarketplace = useCurrentCloudProvider()
  const selectedProduct = useGetProductDetail({
    accountId: accountId || '',
    productId: productId || '',
    cloudMarketplace,
  })

  return cloudMarketplace === 'AZURE' ||
    (!(selectedProduct?.productType === PlanType.CONTAINER) &&
      cloudMarketplace === 'AWS') ? (
    <NoHeaderLayoutWrapper
      component={DetailedViewPrivateOfferWrapper}
      isSidePanelOpened={false}
    />
  ) : (
    <RouteApp
      titleIcon={<HandshakeOutlined fontSize="medium" />}
      title="New private offer"
      component={NewPrivateOfferWrapper}
      actions={CloudAndProductNameTitle}
    />
  )
}

export const AddEditResellerAuthorizationComponent = () => {
  useSyncFlyoutRouteParams()

  const cloudMarketplace = useCurrentCloudProvider()

  if (cloudMarketplace === 'AZURE' || cloudMarketplace === 'AWS') {
    return (
      <NoHeaderLayoutWrapper>
        <AddEditResellerAuthorization />
      </NoHeaderLayoutWrapper>
    )
  }

  return (
    <RouteApp
      titleIcon={<HandshakeOutlined fontSize="medium" />}
      title="New resale authorization"
      component={EditResaleAuthorizationWrapper}
      actions={CloudAndProductNameTitle}
    />
  )
}
